import type {
  GenericSearchParam,
  LinkedInProfile,
  ProfileSearchParams,
} from "common/types";

import { PROFILE_SEARCH_OPTIONS } from "../../../../constants";
import useSearchSourceParams from "../../../../hooks/useSearchSourceParams";
import SearchInputFilter from "../../Filters/SearchInputFilter";
import SelectionTags from "./SelectionTags";
import TypeaheadValues from "./TypeaheadValues";

interface FilterProps {
  optionsKey: keyof typeof PROFILE_SEARCH_OPTIONS;
  accountId: string;
  searchParams: ProfileSearchParams;
  setSearchParams: (searchParams: Partial<ProfileSearchParams>) => void;
}

export default function Filter({
  optionsKey,
  accountId,
  searchParams,
  setSearchParams,
}: FilterProps) {
  const { removeSearchParamValue, setSearchParam } = useSearchSourceParams(
    searchParams,
    setSearchParams,
  );

  // "followers of" holds a single profile, every other filter holds a list
  const appliedValues: Array<GenericSearchParam | LinkedInProfile> =
    optionsKey === "followers_of"
      ? [searchParams.followers_of].filter(Boolean)
      : searchParams[optionsKey];

  const { label } = PROFILE_SEARCH_OPTIONS[optionsKey];

  function onRemoveFilter({ urn }: { urn: string }) {
    if (optionsKey === "followers_of") {
      setSearchParam(optionsKey, null);
      return;
    }
    removeSearchParamValue(optionsKey, { urn });
  }

  return (
    <SearchInputFilter
      label={label}
      renderTagsComponent={(resetInputValue) =>
        appliedValues.length ? (
          <SelectionTags
            selection={appliedValues}
            onRemoveFilter={(option) => {
              onRemoveFilter(option);
              resetInputValue();
            }}
          />
        ) : null
      }
    >
      {(query) => (
        <TypeaheadValues
          optionsKey={optionsKey}
          query={query}
          accountId={accountId}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      )}
    </SearchInputFilter>
  );
}
